// Render a deeplink to a docs article from anywhere in the app (feature
// pages or other MDX articles). Resolves the slug via the manifest +
// useVisibleDocs filter. Behaviour:
//
//   - slug exists + visible        → Mantine Anchor pointing at /docs/<slug>
//   - slug exists + gated for user → renders nothing (so a "Learn more"
//                                    link doesn't show a broken pointer)
//   - slug missing from manifest   → renders nothing in production;
//                                    console.warn in dev. Prevents silent
//                                    rot when an article is renamed or
//                                    removed; complemented by the build-
//                                    time check-doc-links.ts script that
//                                    fails CI on orphaned slugs.

import { Anchor } from "@mantine/core";
import type { ReactNode } from "react";
import { Link } from "@/routing/accountRouting";

import { manifest } from "@/docs/manifest";
import { normalizeSlug } from "@/docs/normalizeSlug";
import { useVisibleDocs } from "@/hooks/useVisibleDocs";

interface Props {
  slug: string;
  children?: ReactNode;
}

export function DocsLink({ slug, children }: Props) {
  const normalized = normalizeSlug(slug);
  const visible = useVisibleDocs();
  const visibleEntry = visible.find((d) => d.slug === normalized);

  if (visibleEntry) {
    return (
      <Anchor component={Link} to={`/docs/${visibleEntry.slug}`}>
        {children ?? visibleEntry.title}
      </Anchor>
    );
  }

  // Silent in production. Dev-mode warn covers the "I just renamed
  // an article and forgot to update a link" case during local work;
  // the CI script (check-doc-links.ts) catches the same on PR.
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV !== "production" &&
    !manifest.some((d) => d.slug === normalized)
  ) {
    // eslint-disable-next-line no-console
    console.warn(`[DocsLink] Unknown slug: "${slug}" (normalized: "${normalized}").`);
  }

  return null;
}
